import React, { useContext, useState } from "react"
import MaterialTable from 'material-table'
import { CircularProgress, Tabs, Tab, Box } from '@material-ui/core'
import ErrorDialog from 'components/common/ErrorDialog'
import PatchedPagination from "components/common/PatchedPagination"
import { commas, createDateString, useRestApi } from 'helpers'
import { ReducerContext } from "App"
import { isEthanolTerminal } from "constants/productTypes"

// Product category tabs configuration
const PRODUCT_CATEGORIES = {
  SAND: { label: 'Sand', filter: ['100M NW', '30/50 NW', '40/70 NW'], isVolumeBased: false },
  CEMENT: { label: 'Cement', filter: ['Cement'], isVolumeBased: false },
  ETHANOL: { label: 'Ethanol', filter: ['Ethanol'], isVolumeBased: true }
}

const InventoryInteractionsTable = () => {
  const { state: { currentTerminal } } = useContext(ReducerContext)
  const [activeTab, setActiveTab] = useState(0)

  // Determine which tabs to show based on terminal
  const showAllTabs = isEthanolTerminal(currentTerminal)
  const availableTabs = showAllTabs
    ? [PRODUCT_CATEGORIES.SAND, PRODUCT_CATEGORIES.CEMENT, PRODUCT_CATEGORIES.ETHANOL]
    : [PRODUCT_CATEGORIES.SAND]

  const currentCategory = availableTabs[activeTab] || PRODUCT_CATEGORIES.SAND

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue)
  }

  // Dynamic column labels based on whether tabs are shown and product type
  const productTypeLabel = showAllTabs ? 'Product Type' : 'Sand Type'
  const isVolumeBased = currentCategory.isVolumeBased

  // Base columns shared across all product types
  const baseColumns = [
    { title: "Inventory Location Name", field: "inventory_id" },
    { title: "Created At", field: "created_at", defaultSort: "desc", render: row => createDateString(row.created_at) },
    { title: "Bill Of Lading ID", field: "bill_of_lading_id" },
    { title: productTypeLabel, field: "sand_type" }
  ]

  // Measurement column differs by product type
  const measurementColumn = isVolumeBased
    ? { title: "Net Gallons", field: "volume_reading", render: row => commas(Number(row.volume_reading) || 0) }
    : { title: "Gross Weight", field: "gross_weight", render: row => commas(row.gross_weight) }

  const columns = [...baseColumns, measurementColumn]

  const { data, loading, error } = useRestApi('api/invinter')

  // Filter by terminal only (for all data at current terminal)
  const terminalData = data?.filter(inv => inv.terminal === currentTerminal) ?? []

  // Calculate counts for each product category (for tab labels)
  const productCounts = showAllTabs
    ? {
        sand: terminalData.filter(inv => PRODUCT_CATEGORIES.SAND.filter.some(f => f === inv.sand_type)).length,
        cement: terminalData.filter(inv => PRODUCT_CATEGORIES.CEMENT.filter.some(f => f === inv.sand_type)).length,
        ethanol: terminalData.filter(inv => PRODUCT_CATEGORIES.ETHANOL.filter.some(f => f === inv.sand_type)).length
      }
    : null

  // Filter by current category for display
  const filteredData = terminalData.filter(inv =>
    currentCategory.filter.some(f => f === inv.sand_type)
  )

  // Map tab keys for product counts lookup
  const TAB_KEYS = ['sand', 'cement', 'ethanol']

  return <>
    {/* Product category tabs - only show if multiple categories available (Binghamton) */}
    {showAllTabs && (
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="inventory interactions category tabs">
          {availableTabs.map((category, index) => {
            const count = productCounts?.[TAB_KEYS[index]]
            const label = count !== undefined ? `${category.label} (${count})` : category.label
            return <Tab key={category.label} label={label} id={`invinter-tab-${index}`} />
          })}
        </Tabs>
      </Box>
    )}

    {loading && <CircularProgress />}
    {error && <ErrorDialog error={error} />}
    {data && <MaterialTable
      title="Inventory Interactions"
      columns={columns}
      data={filteredData}
      components={{ Pagination: PatchedPagination }}
      options={{ paginationType: "normal", filtering: true, pageSize: 10, exportButton: true, headerStyle: { fontSize: '14px' } }}
    />}
  </>
}

export default InventoryInteractionsTable
